import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import PropertyDetails from "./property-details";
import PropertyForm from "./property-form";
import {
  getPropertyRecordsRequest,
  getPropertyDetailsRequest,
  resetPropertyData,
} from "../../../../redux/properties/slice";
import { addPropertyRequest, editPropertyRequest } from "../../../../utils/APICalls";
import { PropertySectionProps, Property, PropertyFormData } from "./types";

export default function PropertySection({
  propertyId,
  onPropertyChange,
  error,
  touched,
  clientId,
  onPropertySuccess,
}: PropertySectionProps) {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [inlineFormMode, setInlineFormMode] = useState<"add" | "edit">("add");
  const [editingProperty, setEditingProperty] =
    useState<PropertyFormData | null>(null);
  const [isLoadingPropertyDetails, setIsLoadingPropertyDetails] =
    useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAddNewInDropdown, setShowAddNewInDropdown] = useState(false);
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { propertyRecords, propertyDetails, loading } = useSelector(
    (state: any) => state.property
  );

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        dispatch(
          getPropertyRecordsRequest({
            searchTerm: searchTerm,
            clientId: clientId,
          })
        );
        setShowDropdown(true);
        setHasSearched(true);
        setShowAddNewInDropdown(true);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (searchTerm.length === 0) {
      setShowDropdown(false);
      setHasSearched(false);
      setShowAddNewInDropdown(false);
    }
  }, [searchTerm, clientId, dispatch]);

  useEffect(() => {
    if (propertyDetails) {
      setIsLoadingPropertyDetails(false);
      // Set searchTerm to show property address when propertyDetails is loaded (for re-order)
      if (propertyDetails.street && propertyDetails.city) {
        const address = propertyDetails.address2 && propertyDetails.address2 !== propertyDetails.street
          ? `${propertyDetails.street}, ${propertyDetails.address2}, ${propertyDetails.city}`
          : `${propertyDetails.street}, ${propertyDetails.city}`;
        setSearchTerm(address);
      }
    }
  }, [propertyDetails]);

  // Load property details when propertyId prop changes (for re-order)
  useEffect(() => {
    if (propertyId && clientId) {
      // Only load if propertyDetails doesn't exist or is for a different property
      if (!propertyDetails || propertyDetails.id !== propertyId) {
        setIsLoadingPropertyDetails(true);
        dispatch(
          getPropertyDetailsRequest({
            id: propertyId,
            clientId: clientId,
          })
        );
      } else {
        // Property details already loaded for this property
        setIsLoadingPropertyDetails(false);
      }
    }
  }, [propertyId, clientId, propertyDetails]); // eslint-disable-line

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setShowAddNewInDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handlePropertySelect = (property: Property) => {
    setSearchTerm("");
    onPropertyChange(property.id);
    setShowDropdown(false);
    setIsLoadingPropertyDetails(true);
    dispatch(
      getPropertyDetailsRequest({
        id: property.id,
        clientId: clientId,
      })
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddNew = () => {
    setInlineFormMode("add");
    setEditingProperty(null);
    setShowInlineForm(true);
    setShowDropdown(false);
  };

  const handleEditProperty = () => {
    setInlineFormMode("edit");
    setEditingProperty(propertyDetails ?? null);
    setShowInlineForm(true);
  };

  const handleInlineFormCancel = () => {
    setShowInlineForm(false);
    setEditingProperty(null);
  };

  const handleInlinePropertySubmit = async (data: PropertyFormData) => {
    try {
      setShowFormLoading(true);
      const response =
        inlineFormMode === "add"
          ? await addPropertyRequest(data)
          : await editPropertyRequest({ ...data, id: editingProperty?.id ?? propertyDetails?.id });

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        const responseData = inlineFormMode === "add" ? response.data : data;
        handlePropertySubmit(responseData);
        setShowInlineForm(false);
        setEditingProperty(null);
      } else if (response?.status === 400 || response?.status === 404) {
        setErrorMessage(
          response?.data?.detail ||
            "An error occurred while saving the property"
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      console.error("Property save error:", error);
      setErrorMessage("An error occurred while saving the property");
      setShowErrorNotification(true);
    } finally {
      setShowFormLoading(false);
    }
  };

  const handlePropertySubmit = (data: any) => {
    if (inlineFormMode === "add") {
      const propertyId = data?.id || data?.propertyId || "";
      if (propertyId) {
        onPropertyChange(propertyId);

        const address = data?.street
          ? `${data.street}, ${data.city}`
          : "New Property";
        setSearchTerm(address);
        setIsLoadingPropertyDetails(true);
        dispatch(
          getPropertyDetailsRequest({
            id: propertyId,
            clientId: clientId,
          })
        );

        if (onPropertySuccess) {
          onPropertySuccess("Property added successfully!");
        }
      } else {
        if (onPropertySuccess) {
          onPropertySuccess(
            "Property added but failed to load details. Please refresh."
          );
        }
      }
    } else {
      if (propertyDetails?.id) {
        setIsLoadingPropertyDetails(true);
        dispatch(
          getPropertyDetailsRequest({
            id: propertyDetails.id,
            clientId: clientId,
          })
        );
      }
      if (onPropertySuccess) {
        onPropertySuccess("Property updated successfully!");
      }
    }
  };

  const formatAddress = (property: Property): string => {
    const parts = [
      property.street,
      property.address2 && property.address2 !== property.street
        ? property.address2
        : null,
      `${property.city}, ${property.state} ${property.zip}`,
      property.country,
    ].filter(Boolean);
    return parts.join("\n");
  };

  const formatLastOrdered = (lastOrderDate: string): string => {
    const date = new Date(lastOrderDate);
    const now = new Date();

    const lastOrderUTC = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    );
    const nowUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );

    const diffDays = Math.round(
      (nowUTC.getTime() - lastOrderUTC.getTime()) / (1000 * 60 * 60 * 24)
    );

    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const dayText =
      diffDays === 0
        ? "Today"
        : diffDays === 1
        ? "1 day ago"
        : diffDays > 1
        ? `${diffDays} days ago`
        : "Future";

    return `${formattedDate} (${dayText})`;
  };

  return (
    <>
      {showFormLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {isLoadingPropertyDetails ? (
        <Loading />
      ) : showInlineForm ? (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 400, mb: 2 }}>
            Property
          </Typography>
          <PropertyForm
            mode={inlineFormMode}
            propertyData={editingProperty ?? undefined}
            onSubmit={handleInlinePropertySubmit}
            loading={showFormLoading}
            clientId={clientId}
            simplifiedView
          />
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleInlineFormCancel}
              sx={{
                color: "primary.main",
                borderColor: "primary.main",
                textTransform: "none",
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if ((window as any).submitPropertyForm) {
                  (window as any).submitPropertyForm();
                }
              }}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                textTransform: "none",
              }}
            >
              {inlineFormMode === "edit" ? "SAVE CHANGES" : "ADD PROPERTY"}
            </Button>
          </Box>
        </Box>
      ) : !propertyDetails ? (
        <>
          <Typography variant="h5" className="section-label">
            Property Details
          </Typography>
          <Box className="user-name-layout">
            <Typography className="label-txt">Property</Typography>
            <Box
              ref={dropdownRef}
              sx={{ width: "calc(100% - 180px)", position: "relative" }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Address or Loan Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  setShowDropdown(true);
                  setShowAddNewInDropdown(true);
                }}
                error={touched && Boolean(error)}
                helperText={touched && error}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                }}
                disabled={!clientId}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "primary.main",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
              {showDropdown && clientId && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: "500px",
                    overflowY: "auto",
                    mt: 1,
                    borderRadius: "8px",
                  }}
                >
                  <List>
                    {/* Show ADD NEW button only when user has clicked on search bar */}
                    {showAddNewInDropdown && (
                      <ListItem
                        component="div"
                        onClick={handleAddNew}
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                          fontWeight: "bold",
                          mt: 1,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Button
                            sx={{
                              color: "primary.main",
                              fontWeight: "500",
                              fontSize: "14px",
                              width: "100%",
                              border: "1px solid",
                              borderColor: "primary.main",
                            }}
                            variant="outlined"
                            startIcon={<Add />}
                          >
                            ADD NEW
                          </Button>
                        </Box>
                      </ListItem>
                    )}

                    {/* Show search results if available */}
                    {propertyRecords?.length > 0 ? (
                      propertyRecords.map((property: Property) => (
                        <ListItem
                          key={property.id}
                          component="div"
                          onClick={() => handlePropertySelect(property)}
                          sx={{
                            borderBottom: "1px solid #f0f0f0",
                            cursor: "pointer",
                            padding: "8px 16px",
                            "&:hover": {
                              backgroundColor: "#fff3e0",
                            },
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <ListItemText>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "start",
                                mb: 1,
                              }}
                            >
                              <Typography className="field-label-txt">
                                Address
                              </Typography>
                              <Typography sx={{ whiteSpace: "pre-line" }}>
                                {formatAddress(property)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "start",
                                mb: 1,
                              }}
                            >
                              <Typography className="field-label-txt">
                                Loan #
                              </Typography>
                              <Typography>{property.loanNumber}</Typography>
                            </Box>
                            {property.lastOrderDate && (
                              <Box
                                sx={{ display: "flex", alignItems: "start" }}
                              >
                                <Typography className="field-label-txt">
                                  Last Ordered
                                </Typography>
                                <Typography>
                                  {formatLastOrdered(property.lastOrderDate)}
                                </Typography>
                              </Box>
                            )}
                          </ListItemText>
                        </ListItem>
                      ))
                    ) : hasSearched && searchTerm.length >= 2 && !loading ? (
                      <ListItem
                        component="div"
                        sx={{
                          padding: "16px",
                          textAlign: "center",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No properties found
                        </Typography>
                      </ListItem>
                    ) : null}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <PropertyDetails
          propertyDetails={propertyDetails}
          onEdit={handleEditProperty}
          onChangeProperty={() => {
            setSearchTerm("");
            onPropertyChange("");
            dispatch(resetPropertyData());
            setIsLoadingPropertyDetails(false);
          }}
        />
      )}

    </>
  );
}
